import { useCallback, useContext, useEffect } from 'react';
import { useDataProvider, useEditContext } from 'react-admin';

import { AppContext } from '@app/contexts';
import { useLogoImage } from '@app/hooks/useLogoImage';
import { FIELD_TYPES, validateFormFields } from '@app/utils/validateUtils';

import { DeleteConfirmation, GenericObject } from '@app/components/common/Table/DefaultTable/types';
import EditView from '@app/components/common/views/EditView';

import { CompanyForm } from './CompanyForm';

const formFields = [
  { key: 'name', type: FIELD_TYPES.MULTILINGUAL, message: 'Name is required' },
  { key: 'preferred_name', type: FIELD_TYPES.MULTILINGUAL, message: 'Full name is required' },
  { key: 'short_name', type: FIELD_TYPES.MULTILINGUAL, message: 'Short name is required' },
  { key: 'industry_id', type: FIELD_TYPES.SELECT, message: 'Industry is required' },
];

const nonRequiredFields = [
  { key: 'description', emptyValue: '', isMultilingual: true },
  { key: 'twitter_handle', emptyValue: '', isMultilingual: false },
  { key: 'content_featuring', emptyValue: '', isMultilingual: true },
  { key: 'disclosure', emptyValue: '', isMultilingual: true },
  { key: 'chix_symbol_id', emptyValue: 0 },
  { key: 'related_topic_ids', emptyValue: [] },
  { key: 'related_vod_ids', emptyValue: [] },
];

export const EditCompany = () => {
  const { handleChangeLoading } = useContext(AppContext);
  const { saving } = useEditContext();
  const dataProvider = useDataProvider();

  const { logoImage, handleChangeImageLogo, transform: logoTransform } = useLogoImage(nonRequiredFields);

  useEffect(() => {
    handleChangeLoading(!!saving);
  }, [saving, handleChangeLoading]);

  const companyTransform = (values: GenericObject) => {
    const v = logoTransform(values);
    /**
     * Workaround to clear input if the user removes a ticker code
     */
    // @ts-ignore Not sure why ts is complaining
    if (v.chix_symbol_id === '') {
      // @ts-ignore
      delete v.chix_symbol_id;
      // @ts-ignore
      delete v.chix_symbol;
    }
    return v;
  };

  const validate = useCallback(
    (values: GenericObject) => validateFormFields({ fields: formFields, values, image: logoImage }),
    [logoImage]
  );

  const resolveDeleteConfirmation = async (companyId: number): Promise<DeleteConfirmation> => {
    const response = await dataProvider.fetchLinks('companies', companyId);
    const links = response?.data;
    return {
      deletable: true,
      message:
        links.Subscribers > 0
          ? `This company is currently being followed by ${links.Subscribers} on the website. Are you sure you want to delete this company?`
          : 'Are you sure you want to delete this company?',
    } as DeleteConfirmation;
  };

  return (
    <div>
      <EditView
        resolveDeleteConfirmation={resolveDeleteConfirmation}
        transform={companyTransform}
        resourceSingular="company"
        validate={validate}
        mutationMode="pessimistic">
        <CompanyForm companyLogo={logoImage} handleChangeCompanyLogo={handleChangeImageLogo} title="EDIT COMPANY" />
      </EditView>
    </div>
  );
};
